import { Box, Button, Typography } from '@mui/material';
import React, { FormEventHandler, useEffect } from 'react';
import { useHistory, useParams } from 'react-router';
import { NavLink as ReactRouterLink } from 'react-router-dom';

import { EntityCheckboxField, EntityTextField, FormHeading, If } from 'app/components';
import { GeneralConstants } from 'app/constants/general-constants';
import { useEntityResolver2 } from 'app/hooks';
import { createPartnerMapQuery, newPartnerMapUnion, PartnerMapQuery, PartnerMapUnion } from 'app/model';
import { partnerMapQueryService } from 'app/services';
import { messagingService } from 'app/services/messaging.service';
import { EntityCollectionOnChange, EntityOnLightChange } from 'app/type';
import { PartnerMapFormQuery } from 'features/cris/partner-map/partner-map-form-query';

export const PartnerMapForm: React.FunctionComponent = () => {
    const { partMapId } = useParams<{ partMapId: string | undefined }>();
    const history = useHistory();

    const { entity, update } = useEntityResolver2(partnerMapQueryService, partMapId);

    const handleSubmit: FormEventHandler = (evt) => {
        evt.preventDefault();
        if (entity) {
            partnerMapQueryService.save(entity).subscribe((saved) => {
                messagingService.showSuccess('Saved');
                history.replace(`${saved.id}`);
                update(saved);
            });
        }
    };

    useEffect(() => {
        if (!entity) {
            update(newPartnerMapUnion());
        }
    }, [entity, update]);

    if (!entity) {
        // until we have the data object set render nothing
        return GeneralConstants.RENDER_NOTHING;
    }

    const handleEntityUpdate: EntityOnLightChange<PartnerMapUnion, unknown> = (evt, name, value) => {
        update({
            ...entity,
            [name]: value,
            remarks: 'queries' === name ? buildRemarks(value as PartnerMapQuery[]) : entity.remarks,
        });
    };

    const handleAddQuery = (): void => {
        handleEntityUpdate(undefined, 'queries', [...entity.queries, createPartnerMapQuery()]);
    };

    const handleRemoveQuery = (index: number): void => {
        const change = [...entity.queries];
        change.splice(index, 1);

        handleEntityUpdate(undefined, 'queries', change);
    };

    const handleQueryUpdate: EntityCollectionOnChange<PartnerMapQuery> = (evt, index, query) => {
        const copy = [...entity.queries];
        copy[index] = query;
        handleEntityUpdate(evt, 'queries', copy);
    };

    return (
        <form onSubmit={handleSubmit}>
            <Box display='flex' flexDirection='column' sx={{ gap: '1em' }} className='mx-1 my-1'>
                <FormHeading entityName='Partner Map' editedId={partMapId} />
                <Box>
                    <EntityCheckboxField value={entity} name='active' label='Active' onChange={handleEntityUpdate} />
                    <Button
                        type='button'
                        variant='outlined'
                        color='secondary'
                        size='small'
                        className='mb-0'
                        onClick={handleAddQuery}
                    >
                        Add Union
                    </Button>
                    <Button type='submit' variant='contained' color='primary' size='small' className='ml-1 mb-0'>
                        Save
                    </Button>
                    <Button
                        type='button'
                        variant='outlined'
                        color='warning'
                        size='small'
                        className='ml-1 mb-0'
                        component={ReactRouterLink}
                        to='/cris/partner-maps'
                    >
                        Cancel
                    </Button>
                </Box>
                <Box display='flex' flexDirection='row' sx={{ gap: '1em' }}>
                    <If condition={!entity.id}>
                        <EntityTextField
                            label='Part Id'
                            name='desiredPartId'
                            value={entity}
                            onChange={handleEntityUpdate}
                        />
                    </If>
                    <EntityTextField sx={{ flexGrow: 1 }} label='Remarks' name='remarks' value={entity} disabled />
                </Box>
            </Box>

            <Typography variant='h5'>Unions</Typography>
            <If condition={0 === entity.queries.length}>
                <Typography>
                    Click <strong>Add Union</strong> to add a union to this map.
                </Typography>
            </If>
            {entity.queries.map((query, index) => (
                <PartnerMapFormQuery
                    key={index}
                    index={index}
                    query={query}
                    onChange={handleQueryUpdate}
                    onRemove={handleRemoveQuery}
                />
            ))}
        </form>
    );
};

const buildRemarks = (queries: PartnerMapQuery[]): string => {
    const remarks: string[] = [];

    remarks.push(...queries.map((query) => query.remarks ?? ''));

    return remarks.join('\nUNION\n');
};
